import React, { useEffect, useState, useMemo } from 'react';
import { getNeighborhoodList } from '@/API/apiService';
import Picture from '@/image/hotel/picture4.png'; // fallback用画像をインポート
import { LocalizedLink } from 'src/components/common/LocalizedLink';
import { useParams } from 'react-router-dom';

// === (1) 型定義 ===
interface Tag {
  id: number;
  name: string;
  name_en: string;
}

interface Area {
  id: number;
  name: string;
  name_en: string;
}

interface Neighborhood {
  neighborhood_id: number;
  title: string;
  title_en: string;
  list_title: string;
  list_title_en: string;
  header_image_url: string;
  date_time: string;
  areas: Area[];
  tags: Tag[];
}

export const Explore: React.FC = () => {
  // === (2) Neighborhood, 全タグ一覧, 全エリア一覧を管理 ===
  const [neighborhoods, setNeighborhoods] = useState<Neighborhood[]>([]);
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [allAreas, setAllAreas] = useState<Area[]>([]);

  // === (3) 選択されているタグID、エリアIDの配列 ===
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [selectedAreaIds, setSelectedAreaIds] = useState<number[]>([]);

  // === (4) 言語フラグ (例: false=日本語, true=英語) ===
  const { lang } = useParams<{
    lang?: string;
  }>();
  const useEnglish = lang?.toLowerCase() === 'en';

  // ------------------------------------------------------------------
  // (5) コンポーネントマウント時にデータ取得
  // ------------------------------------------------------------------
  useEffect(() => {
    getNeighborhoodList()
      .then((res) => {
        if (res.data && Array.isArray(res.data.data)) {
          const data: Neighborhood[] = res.data.data;
          setNeighborhoods(data);

          // 全タグ抽出
          const extractedTags: Tag[] = [];
          data.forEach((n) => {
            n.tags?.forEach((tag) => {
              if (!extractedTags.some((t) => t.id === tag.id)) {
                extractedTags.push(tag);
              }
            });
          });
          setAllTags(extractedTags);

          // 全エリア抽出
          const extractedAreas: Area[] = [];
          data.forEach((n) => {
            n.areas?.forEach((area) => {
              if (!extractedAreas.some((a) => a.id === area.id)) {
                extractedAreas.push(area);
              }
            });
          });
          setAllAreas(extractedAreas);
        }
      })
      .catch((error) => {
        console.error('Failed to fetch neighborhood list:', error);
      });
  }, []);

  // ------------------------------------------------------------------
  // (6) タグ、エリア選択トグル
  // ------------------------------------------------------------------
  const toggleTagSelection = (tagId: number) => {
    setSelectedTagIds(
      (prev) =>
        prev.includes(tagId)
          ? prev.filter((id) => id !== tagId) // 解除
          : [...prev, tagId] // 追加
    );
  };

  const toggleAreaSelection = (areaId: number) => {
    setSelectedAreaIds(
      (prev) =>
        prev.includes(areaId)
          ? prev.filter((id) => id !== areaId) // 解除
          : [...prev, areaId] // 追加
    );
  };

  // ------------------------------------------------------------------
  // (6-1) クリアボタン用ハンドラー関数
  // ------------------------------------------------------------------
  const clearFilters = () => {
    setSelectedTagIds([]);
    setSelectedAreaIds([]);
  };

  // ------------------------------------------------------------------
  // (7) フィルタリング (Areas と Tags の AND)
  //     - AreasはOR
  //     - TagsはOR
  //     - 両者はAND
  // ------------------------------------------------------------------
  const filteredNeighborhoods = useMemo(() => {
    return neighborhoods.filter((n) => {
      // ▼ A. エリアチェック
      if (selectedAreaIds.length > 0) {
        const matchArea = n.areas?.some((a) => selectedAreaIds.includes(a.id));
        if (!matchArea) return false;
      }

      // ▼ B. タグチェック
      if (selectedTagIds.length > 0) {
        const matchTag = n.tags?.some((t) => selectedTagIds.includes(t.id));
        if (!matchTag) return false;
      }

      return true;
    });
  }, [neighborhoods, selectedTagIds, selectedAreaIds]);

  // ------------------------------------------------------------------
  // (8) 全カード表示 (カード全体を <LocalizedLink> でラップ)
  // ------------------------------------------------------------------
  const renderNeighborhoodList = () => {
    return (
      <div className="mt-5 grid grid-cols-3 md:grid-cols-4 gap-4">
        {filteredNeighborhoods.map((n) => {
          const displayTitle = useEnglish && n.title_en ? n.title_en : n.title;
          const displayListTitle =
            useEnglish && n.list_title_en ? n.list_title_en : n.list_title;

          return (
            <LocalizedLink
              to={`/magazine/${n.neighborhood_id}`}
              key={n.neighborhood_id}
              className="cursor-pointer block"
            >
              <img
                src={n.header_image_url}
                alt={displayListTitle}
                className="w-full h-40 object-cover mb-2"
                onError={(e) => {
                  (e.target as HTMLImageElement).src =
                    'https://dummyimage.com/400x300/000/fff';
                }}
              />
              {/* タイトル (全部黒, boldなし) */}
              <p className="text-black text-sm mb-1">{displayTitle}</p>
              {/* サブタイトル (全部黒, boldなし) */}
              {/* <p className="text-black text-[8px] mb-2">{displayListTitle}</p> */}

              {/* エリア表示 */}
              <div className="text-black flex flex-wrap mb-1 underline text-[9px]">
                {n.areas?.map((a, idx) => (
                  <span key={a.id}>
                    <span className="mr-1">
                      {useEnglish ? a.name_en : a.name}
                    </span>
                    {/* カンマ区切り */}
                    {idx < n.areas.length - 1 && <span>, </span>}
                  </span>
                ))}
              </div>
              {/* タグ表示 */}
              <div className="text-black flex flex-wrap underline text-[9px]">
                {n.tags?.map((t, idx) => (
                  <span key={t.id}>
                    <span className="mr-1">
                      {useEnglish ? t.name_en : t.name}
                    </span>
                    {idx < n.tags.length - 1 && <span>, </span>}
                  </span>
                ))}
              </div>
            </LocalizedLink>
          );
        })}
      </div>
    );
  };

  // ------------------------------------------------------------------
  // (9) 選択中かどうかでテキスト色を変える
  // ------------------------------------------------------------------
  const getTextColorClass = (isSelected: boolean) =>
    isSelected ? 'text-green-600' : 'text-black';

  // ------------------------------------------------------------------
  // (10) エリア一覧を「span + underline + cursor-pointer」で表示
  // ------------------------------------------------------------------
  const renderAreasAsText = () => {
    return (
      <p className="text-sm text-start">
        {allAreas.map((area, index) => {
          const isSelected = selectedAreaIds.includes(area.id);
          return (
            <span key={area.id}>
              <span
                className={`underline cursor-pointer ${getTextColorClass(
                  isSelected
                )}`}
                onClick={() => toggleAreaSelection(area.id)}
              >
                {useEnglish ? area.name_en : area.name}
              </span>
              {/* カンマ区切り */}
              {index < allAreas.length - 1 && <span>, </span>}
            </span>
          );
        })}
      </p>
    );
  };

  // ------------------------------------------------------------------
  // (11) タグ一覧を「span + underline + cursor-pointer」で表示
  // ------------------------------------------------------------------
  const renderTagsAsText = () => {
    return (
      <p className="text-sm text-start">
        {allTags.map((tag, index) => {
          const isSelected = selectedTagIds.includes(tag.id);
          return (
            <span key={tag.id}>
              <span
                className={`underline cursor-pointer ${getTextColorClass(
                  isSelected
                )}`}
                onClick={() => toggleTagSelection(tag.id)}
              >
                {useEnglish ? tag.name_en : tag.name}
              </span>
              {index < allTags.length - 1 && <span>, </span>}
            </span>
          );
        })}
      </p>
    );
  };

  // ------------------------------------------------------------------
  // (12) 最終的なレンダリング
  // ------------------------------------------------------------------
  return (
    <div className="max-w-[800px] mx-auto text-black">
      <div className="text-center mb-5 text-lg">(EXPLORE)</div>
      {/* エリア選択 */}
      <div className="mb-4 text-start">
        <h2 className="text-black text-[9px] mb-4">Neighborhoods</h2>
        {renderAreasAsText()}
      </div>
      {/* タグ選択 */}
      <div className="mb-4 text-start">
        <h2 className="text-black text-[9px] mb-4">Tags</h2>
        {renderTagsAsText()}
      </div>
      {/* クリアボタン */}
      <div className="flex justify-end mb-4 md:justify-center">
        <div
          onClick={clearFilters}
          className="px-4 py-2 text-sm text-black cursor-pointer underline"
        >
          (clear ×)
        </div>
      </div>
      {/* フィルタ結果一覧 */}
      {renderNeighborhoodList()}
    </div>
  );
};
